import { useCallback, useEffect, useRef, useState, FC } from 'react'
import { Conversation, Message, Paginator } from '@twilio/conversations'
import { differenceInMinutes, isSameDay, isSameMinute } from 'date-fns'

import { useParticipantsTyping, useConversationDisplayName } from 'features/MerinioChat/hooks'

import { ConversationFooter, ConversationNav, ConversationBody, MessageItem, AnchorBottom, Typing } from './components'

import styles from './style.module.scss'

type Props = {
  conversation: Conversation
  setActiveConversation: (conversation: Conversation | null) => void
  currentUser: MData.CurrentUser['userInfo']
  deleteConversation: (conversation: Conversation) => Promise<void>
  setConversationsUnreadMessagesMap: React.Dispatch<React.SetStateAction<Map<string, number>>>
}

const DISPLAY_PARTICIPANT_MINUTES_THRESHOLD = 15

const ConversationThread: FC<Props> = ({ conversation, setActiveConversation, currentUser, deleteConversation, setConversationsUnreadMessagesMap }) => {
  const conversationRef = useRef<HTMLDivElement>(null)
  const elementsRef = useRef<(HTMLDivElement | null)[]>([])
  const bottomRef = useRef<HTMLDivElement>(null)
  const paginator = useRef<Paginator<Message> | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [scrollY, setScrollY] = useState<number | undefined>(undefined)
  const { displayParticipantsTyping } = useParticipantsTyping(conversation)
  const { displayName, isLoading } = useConversationDisplayName(conversation)

  const scrollToElement = useCallback((element: HTMLDivElement | null) => {
    if (!element) return
    element.scrollIntoView()
  }, [])
  const getMessages = useCallback(async () => {
    const messages = await conversation.getMessages()
    paginator.current = messages
    setMessages(paginator.current.items)
    await conversation.setAllMessagesRead()
  }, [conversation])

  useEffect(() => {
    ;(async () => {
      await getMessages()
      scrollToElement(bottomRef.current)
      await conversation.setAllMessagesRead()
      setConversationsUnreadMessagesMap(prev => new Map(prev).set(conversation.sid, 0))
    })()
    return () => {
      setConversationsUnreadMessagesMap(prev => new Map(prev).set(conversation.sid, 0))
    }
  }, [conversation, getMessages, scrollToElement, setConversationsUnreadMessagesMap])

  const getMoreMessages = useCallback(async () => {
    if (paginator.current?.hasPrevPage) {
      const prevMessages = await paginator.current.prevPage()
      paginator.current = prevMessages

      setMessages(messages => {
        return [...prevMessages.items, ...messages]
      })
      return prevMessages
    }
  }, [])

  useEffect(() => {
    if (scrollY === 0) {
      ;(async () => {
        const messages = await getMoreMessages()
        if (messages?.items && elementsRef.current?.length) {
          const element = elementsRef.current[messages.items.length - 1]
          scrollToElement(element)
        }
      })()
    }
  }, [scrollY, getMoreMessages, scrollToElement])

  // listen to messages events and update messages state
  useEffect(() => {
    conversation.on('messageAdded', getMessages)
    conversation.on('messageUpdated', getMessages)
    conversation.on('messageRemoved', getMessages)

    return () => {
      conversation.off('messageAdded', getMessages)
      conversation.off('messageUpdated', getMessages)
      conversation.off('messageRemoved', getMessages)
    }
  }, [conversation, getMessages])

  useEffect(() => {
    const currentRef = conversationRef.current
    const handleScroll = () => {
      setScrollY(currentRef?.scrollTop)
    }

    currentRef?.addEventListener('scroll', handleScroll)

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleSendMessage = useCallback(
    async (message: HTMLTextAreaElement | null) => {
      if (!message || !message.value.trim().length) return
      const messageToSend = message.value

      message.value = ''

      await conversation.sendMessage(messageToSend)

      if (messages.length && messages[messages.length - 1].author === currentUser?._id) {
        scrollToElement(bottomRef.current)
      }
    },
    [conversation, currentUser, messages, scrollToElement]
  )

  const handleDeleteConversation = useCallback(async () => {
    setActiveConversation(null)
    await deleteConversation(conversation)
  }, [conversation, setActiveConversation, deleteConversation])

  if (!currentUser) return null

  return (
    <div className={styles.grid}>
      <ConversationNav
        conversation={conversation}
        isAuthor={conversation.createdBy === currentUser?._id}
        handleDeleteConversation={handleDeleteConversation}
        setActiveConversation={setActiveConversation}
        displayName={displayName}
        isLoading={isLoading}
      />
      <ConversationBody conversationRef={conversationRef}>
        {messages.map((message, index) => {
          const sameAuthorAsPreviousMessage = messages[index - 1]?.author === message.author
          const sameDateAsPreviousMessage = isSameDay(message.dateCreated || 0, messages[index - 1]?.dateCreated || 0)
          const sameMinuteAsPreviousMessage = isSameMinute(message.dateCreated || 0, messages[index - 1]?.dateCreated || 0)
          const lessThanMinutesThresholdFromPreviousMessage =
            differenceInMinutes(message.dateCreated || 0, messages[index - 1]?.dateCreated || 0) < DISPLAY_PARTICIPANT_MINUTES_THRESHOLD
          return (
            <div ref={ref => (elementsRef.current[index] = ref)} key={message.sid}>
              <MessageItem
                sameAuthorAsPreviousMessage={sameAuthorAsPreviousMessage}
                sameDateAsPreviousMessage={sameDateAsPreviousMessage}
                sameMinuteAsPreviousMessage={sameMinuteAsPreviousMessage}
                lessThanMinutesThresholdFromPreviousMessage={lessThanMinutesThresholdFromPreviousMessage}
                isAuthor={currentUser._id === message.author}
                message={message}
              />
            </div>
          )
        })}
        <Typing displayParticipantsTyping={displayParticipantsTyping} />
        <AnchorBottom bottomRef={bottomRef} />
      </ConversationBody>
      <ConversationFooter conversation={conversation} handleSendMessage={handleSendMessage} />
    </div>
  )
}
export { ConversationThread }
